import { defaultTimeZone, hoursInDay } from '../constants/date';
import moment from 'moment-timezone';

/**
 * @module dateUtils
 * @description Utilities for date, time and timezone manipulation.
 * @see https://en.wikipedia.org/wiki/List_of_tz_database_time_zones
 */

/**
 * @description Will return formatted date according to passed format,
 * and add timezone abbreviation if current user`s time zone is different
 *
 * @param {string|moment.Moment|Date} start_date
 * @param {string} format
 * @param {string} [timezone='America/New_York'] - {@see defaultTimeZone}
 * @param {string|moment.Moment|Date} end_date
 * @return {string}
 */
export function renderDateWithTimeZone (start_date, format, timezone, end_date) {
  const formattedStartDate = getDateWithOffset(start_date, timezone).format(format);
  let dateToRender = formattedStartDate;
  if (end_date) {
    const formattedEndDate = getDateWithOffset(end_date, timezone).format(format);
    dateToRender = `${formattedStartDate} - ${formattedEndDate}`;
  }

  const timeZonesAreDifferent = !isTimeZoneMatchesCurrent(timezone);

  if (timeZonesAreDifferent) {
    dateToRender += ` (${getTimezoneAbbr(start_date, timezone)})`;
  }

  return dateToRender;
}

/**
 *
 * @param {string|moment.Moment|Date} date
 * @param {string} [timezone='America/New_York'] - {@see defaultTimeZone}
 * @return {moment.Moment}
 */
export function getDateWithOffset (date, timezone) {
  timezone = timezone || defaultTimeZone;

  return moment.tz(date, timezone);
}

/**
 *
 * @param {string} timezone
 * @return {boolean}
 */
export function isTimeZoneMatchesCurrent (timezone) {
  const currentTimeZone = moment.tz.guess(true);

  return timezone === currentTimeZone;
}

/**
 *
 * @param {string|moment.Moment|Date} date
 * @param {string} [timezone='America/New_York'] - {@see defaultTimeZone}
 * @return {string}
 * @example
 *  getTimezoneAbbr('Wed Aug 15 2018 08:22:55', 'America/Los_Angeles'); //-> PDT
 */
export function getTimezoneAbbr (date, timezone) {
  timezone = timezone || defaultTimeZone;

  const dateTime = getDateWithOffset(date, timezone);

  return moment.tz.zone(timezone).abbr(dateTime);
}

/**
 *
 * @param {string} [timezone='America/New_York'] - {@see defaultTimeZone}
 * @return {moment.Moment}
 */
export function getNowInTimeZone (timezone) {
  timezone = timezone || defaultTimeZone;

  return moment().tz(timezone);
}

/**
 *
 * @param {string|moment.Moment|Date} date
 * @param {string} [timezone='America/New_York'] - {@see defaultTimeZone}
 * @return {number}
 * @example
 *  getTimeZoneOffset('2018-08-27T01:00:00', 'America/New_York'); //-> -4
 */
const secondsInMinute = 60;

export function getTimeZoneOffset (date, timezone = defaultTimeZone) {
  date = date || getNowInTimeZone(timezone);
  return (moment.tz(date, timezone).utcOffset()) / secondsInMinute;
}

/**
 *
 * @param {string|moment.Moment|Date} start_date
 * @param {string} time_zone_id
 * @param {number} [hoursOffset = hoursInDay]
 * @return {boolean}
 */
export function isJobDateSoonOrPast (start_date, time_zone_id, hoursOffset = hoursInDay) {
  const startDate = getDateWithOffset(start_date, time_zone_id);
  const now = getNowInTimeZone(time_zone_id);
  const timeDiff = startDate.diff(now, 'hours');

  return timeDiff < hoursOffset;
}

/**
 *
 * @param {moment.Moment} date // with current timezone
 * @param {string} time_zone_id
 * @return {moment.Moment} // the same time with needed timezone
 */
export function useTimezoneForDate (date, time_zone_id) {
  return moment(date).tz(time_zone_id, true);
}



// WEBPACK FOOTER //
// ./src/helpers/timezones.js